<template>
	<!-- Generate document dialog -->
	<Dialog
			class="p-fluid" 
			style="min-width:60%;width:1780px;max-width:95%;height:95%" 
			:position="'center'" 
			:header="getPopupHeader" 
			:showHeader="true" 
			:modal="true" 
			:maximizable="true" 
			v-model:visible="show" 
			>
		<TabView v-model:activeIndex="activeTab">
			<TabPanel v-if="showDocumentPanel">
				<template #header>
					<i class="pi pi-file-pdf"></i>&nbsp;
					<span>Document</span>
				</template>
				<div class="flex justify-content-center">
					<div id="document" class="d-inline-block">
						<component :is="document.templateName"
									:propertyData="propertyData"
									:reportContact="reportContact"
									:service="toggledService"
									:issuedDate="issuedDate"
									@updateServicesCount="servicesCount = $event;"
									@updateDateOfEvacuation="dateOfEvacuation = $event;" />
					</div>
				</div>
			</TabPanel>
			<TabPanel v-if="showEmailPanel">
				<template #header>
					<i class="pi pi-envelope"></i>&nbsp;
					<span>Email</span>
				</template>
				<div>
					<EmailTemplate ref="EmailTemplate"
									:propertyData="propertyData" 
									:recipientCompanyId="recipientCompanyId"
									:emailTemplateType="selectedEmailTemplate"
									:service="toggledService"
									:issuedDate="issuedDate"
									@updateIsEmailSending="isEmailSending = $event;"
									@updateWasEmailSent="wasEmailSent = $event;"
									@updateIsEmailTemplateLoading="isEmailTemplateLoading = $event;"
									@reloadData="reloadData"
									@updateShow="show = $event;"/>
				</div>
			</TabPanel>
		</TabView>
		<!--Footer-->
		<template #footer>
			<div class="flex justify-content-end mt-3">
				<div class="mr-2 text-left" v-if="showIssuedDate">
					<span>Issue Date</span>
					<Calendar v-model="issuedDate" dateFormat="d MM yy" :showButtonBar="true" placeholder="Issue Date" />
				</div>
				<!--Footer for document generation panel-->
				<template v-if="showDocumentPanel && activeTab==0">
					<div class="mr-2 align-self-end" style="width:120px;" v-if="!useAutoScale">
						<span class="p-input-icon-right">
							<InputText v-model="documentScaling" type="number" class="text-right" />
							<i class="pi pi-percentage" />
						</span>
					</div>
					<div class="align-self-end">
						<Button :label="(isGeneratingDocument? 'Generating...' : 'Generate')" @click="generateDocument" :disabled="isGeneratingDocument || isEmailSendingDisabled" :class="isEmailSendingDisabled ? 'disabled-feature':''" />
					</div>
				</template>

				<!--Footer for Email panel -->
				<template v-else-if="showEmailPanel">
					<div class="align-self-end">
						<!--This button calls a function within the EmailTemplate component in order to initiate the email generation and send.-->
						<Button :label="(isEmailSending? 'Sending...' : 'Send Email')" @click="onSendEmailClick" :disabled="wasEmailSent || isEmailTemplateLoading || isEmailSendingDisabled" :class="isEmailSendingDisabled ? 'disabled-feature':''" />
					</div>
				</template>
			</div>
		</template>
	</Dialog>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { GeneratePdfRequest, PropertyData, EmailTemplateType, PropertyServiceData, ReportContact } from '../api-client/api-client';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { GetCompassCompanyID, GetDocumentTypes } from '@/utils/CompassConstants';
	import { format } from 'date-fns';
	import Toast from '@/utils/Toast';
	import isStringNullOrEmpty from '@/utils/StringUtils';
	import { EmailTemplateComponent } from '@/utils/CompassConstants';

	@Options({
		name: 'DocumentGeneration',
		components: {
		},
		props: {
			propertyData: null,
			selectedDocumentTemplate: null,
			selectedEmailTemplate: null,
			toggledService: null,
			recipientCompanyId: null,
			initialActiveTab: 0,
			isEmailSendingDisabled: false,
		},
		watch: {
			show(value: boolean) {
				this.$emit('updateWasEmailSent', this.wasEmailSent);
				this.$emit('updateShowDocumentGenerationComponent', value);
			},
		},
	})
	export default class DocumentGeneration extends Vue {
		propertyData!: PropertyData;
		selectedDocumentTemplate!: string | null;
		selectedEmailTemplate!: EmailTemplateType | null;
		reportContact: ReportContact = new ReportContact();

		// Document component
		toggledService: PropertyServiceData = new PropertyServiceData;

		// Vars
		show = true;
		documentDialog = false;
		isGeneratingDocument = false;
		documentScaling = 100;
		compassCompanyID = GetCompassCompanyID();
		servicesCount = 0;
		documentTypes = GetDocumentTypes();
		dateOfEvacuation = new Date();
		initialActiveTab = 0;
		activeTab = 0;
		isEmailSending = false;
		wasEmailSent = false;
		isEmailTemplateLoading = false;
		recipientCompanyId!: number;
		issuedDate = new Date();

		created() {
			this.activeTab = this.initialActiveTab == undefined ? 0 : this.initialActiveTab;
			this.getReportContactData();
		}

		async getReportContactData() {
			try {
				const contactApi = CompassApi.contactApi(this.$store);
				this.reportContact = await contactApi.getReportContact();

			} catch (error: any) {
				Toast.ApiError("Could not fetch contact data", error);
			}
		}

		generateDocument() {
			const content = document.getElementById("document")?.outerHTML;
			if (!content) {
				return;
			}

			const docModel = new GeneratePdfRequest();
			docModel.propertyId = this.propertyData.propertyId;
			docModel.filename = this.getFileNameForGeneratedDocument();
			docModel.documentHeader = document.head.outerHTML;
			docModel.htmlContent = content;
			docModel.scalingPercentage = this.scaling;

			// Fit to one page only, used for BWOF and Report in Lieu
			if (this.useAutoScale)
				docModel.fitToPageCount = 1;

			const apiClient = CompassApi.documentApi(this.$store);
			this.isGeneratingDocument = true;
			apiClient.generateDocument(docModel)
				.then(result => {
					this.isGeneratingDocument = false;
					this.show = false;

					// Set the updated documents list
					this.propertyData.documents = result;

					Toast.RemoveAllGroups();
					Toast.Success('Generate Document', "'" + docModel.filename + "' has been generated and can now be found in the document list for this property.");

					this.$emit('updateActiveDocumentPanelTab', 0)
				})
				.catch(error => {
					this.isGeneratingDocument = false;
					Toast.ApiError('Error generating ' + this.selectedDocumentTemplate, error);
				});
		}

		getFileNameForGeneratedDocument() {
			let supplierName = "";

			let fileDate = new Date().getFullYear().toString();

			if (!this.toggledService && this.isBwof) {

				const matchingService = this.getBwofService();

				if (matchingService && matchingService.supplierName && matchingService.whenNextServiceAnniversary) {
					supplierName = matchingService.supplierName
					fileDate = format(matchingService.whenNextServiceAnniversary, 'yyyy').toString();
				}
			}

			if (this.toggledService && this.toggledService.supplierName)
				supplierName = this.toggledService.supplierName;

			if (isStringNullOrEmpty(supplierName))
				console.error("Could not set supplier name")

			if (this.isEvacuationReport) {
				fileDate = format(this.dateOfEvacuation, 'yyyy-MM-dd').toString();
			}

			if (this.isAmendmentToComplianceSchedule) {
				fileDate = format(this.issuedDate, 'yyyy-MM-dd').toString();
				return `${this.propertyData.propertyId} Form 11 - Application for Amendment to Compliance Schedule ${fileDate}.pdf`;
			}

			return `${this.propertyData.propertyId} ${supplierName} ${this.document ? this.document.label : ''} ${fileDate}.pdf`;
		}

		async onSendEmailClick() {
			const emailTemplateRef = this.$refs.EmailTemplate as unknown as EmailTemplateComponent;
			await emailTemplateRef.validateAndSendEmail();
			this.$emit('refreshSentEmails', true);
		}

		reloadData() {
			this.$emit('reloadData');
		}

		getBwofService() {
			if (!this.propertyData.services)
				return undefined;

			return this.propertyData.services.find((service) => service.serviceName === "BWOF");
		}

		get isBwof() {
			return this.selectedDocumentTemplate == 'BwofTemplate';
		}

		get is12ACertificate() {
			return this.selectedDocumentTemplate?.includes("CertificateOfCompliance");
		}

		get isEvacuationReport() {
			return this.selectedDocumentTemplate == 'EvacuationReport';
		}

		get isReportInLeuOfForm12A() {
			return this.selectedDocumentTemplate == 'ReportInLieuOfForm12ATemplate';
		}

		get isAmendmentToComplianceSchedule() {
			return this.selectedDocumentTemplate == 'ComplianceTemplate';
		}

		get document() {
			let doc = this.documentTypes.main.find(d => d.templateName == this.selectedDocumentTemplate);

			if (doc)
				return doc;

			doc = this.documentTypes.supplier.find(d => d.templateName == this.selectedDocumentTemplate);
			if (doc)
				return doc;
			return {
				label: this.selectedDocumentTemplate,

			};
		}

		get scaling() {
			if (!this.useAutoScale) {
				return this.documentScaling;
			}

			// TODO: If it's a BWOF or Report in Lieu use computed scalings
			if (this.servicesCount > 7 && this.servicesCount <= 9)
				return 96;

			if (this.servicesCount > 9 && this.servicesCount <= 11)
				return 93;

			if (this.servicesCount > 11 && this.servicesCount <= 13)
				return 91;

			if (this.servicesCount > 13 && this.servicesCount <= 15)
				return 88;

			if (this.servicesCount > 15)
				return 86;

			return 100;
		}

		get useAutoScale() {
			return this.isBwof || this.isReportInLeuOfForm12A;
		}

		get isAuthorityEmailTemplate() {
			return this.selectedEmailTemplate == EmailTemplateType.Email_AuthorityOverdueBwofNotification;
		}

		get getPopupHeader() {
			if (this.isAuthorityEmailTemplate)
				return "Regional Authority Email";

			return this.showDocumentPanel && this.showEmailPanel ? 'Automated Email with Generated Document' :
				this.showDocumentPanel && !this.showEmailPanel ? 'Generated Document' :
				'Automated Email';
		}

		get showDocumentPanel() {
			return this.selectedDocumentTemplate != null;
		}

		get showEmailPanel() {
			return this.selectedEmailTemplate != null;
		}

		get showIssuedDate() {
			return this.recipientCompanyId == this.compassCompanyID;
		}
	}
</script>

<style scoped lang="scss">
</style>